import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface AdminSupportPanelProps {
  admin: any;
  onUnreadChange: (count: number) => void;
}

export default function AdminSupportPanel({ admin, onUnreadChange }: AdminSupportPanelProps) {
  const [providers, setProviders] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      loadMessages(selectedProvider.id);
      
      // Subscribe to new messages
      const channel = supabase
        .channel('admin_support_chat')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_messages',
            filter: `conversation_id=eq.${selectedProvider.id}`
          },
          (payload) => {
            setMessages(prev => [...prev, payload.new]);
            scrollToBottom();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [selectedProvider]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadProviders = async () => {
    const { data } = await supabase
      .from('provider_profiles')
      .select('*')
      .order('full_name');

    if (data) {
      // Load unread counts and last message for each provider
      const providersWithUnread = await Promise.all(
        data.map(async (provider) => {
          const { count } = await supabase
            .from('admin_messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', provider.id)
            .eq('recipient_type', 'admin')
            .eq('is_read', false);

          // Get last message timestamp
          const { data: lastMsg } = await supabase
            .from('admin_messages')
            .select('created_at, message')
            .eq('conversation_id', provider.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          return { 
            ...provider, 
            unreadCount: count || 0,
            lastMessage: lastMsg?.message || null,
            lastMessageTime: lastMsg?.created_at || null
          };
        })
      );

      // Sort by: unread messages first, then by last message time
      const sortedProviders = providersWithUnread.sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
        if (a.lastMessageTime && b.lastMessageTime) {
          return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
        }
        if (a.lastMessageTime) return -1;
        if (b.lastMessageTime) return 1;
        return a.full_name.localeCompare(b.full_name);
      });

      setProviders(sortedProviders);
      
      // Update total unread count
      const totalUnread = sortedProviders.reduce((sum, p) => sum + p.unreadCount, 0);
      onUnreadChange(totalUnread);
    }
  };

  const loadMessages = async (providerId: string) => {
    const { data } = await supabase
      .from('admin_messages')
      .select('*')
      .eq('conversation_id', providerId)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data);
      
      // Mark messages as read
      await supabase
        .from('admin_messages')
        .update({ is_read: true })
        .eq('conversation_id', providerId)
        .eq('recipient_type', 'admin')
        .eq('is_read', false);

      // Reload providers to update unread counts
      loadProviders();
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedProvider) return;

    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('admin_messages')
      .insert([{
        conversation_id: selectedProvider.id,
        sender_id: user?.id,
        sender_type: 'admin',
        recipient_id: selectedProvider.user_id,
        recipient_type: 'provider',
        message: newMessage
      }]);

    if (!error) {
      setNewMessage('');
    }

    setLoading(false);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + 
             date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
  };

  const filteredProviders = providers.filter(provider =>
    provider.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    provider.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Live Support</h2>
        <p className="text-gray-600 mt-1">Provide support to service providers</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 250px)' }}>
        <div className="flex h-full">
          {/* Providers List */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Service Providers</h3>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search providers..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B3C5D] focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-200">
              {filteredProviders.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-user-search-line text-4xl text-gray-300 mb-2 w-10 h-10 flex items-center justify-center mx-auto"></i>
                  <p className="text-sm">No providers found</p>
                </div>
              ) : (
                filteredProviders.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => setSelectedProvider(provider)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedProvider?.id === provider.id ? 'bg-blue-50 border-l-4 border-teal-600' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-[#0B3C5D] font-semibold">
                          {provider.full_name[0].toUpperCase()}
                        </div>
                        {provider.unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                            {provider.unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className={`font-medium truncate ${provider.unreadCount > 0 ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>
                            {provider.full_name}
                          </p>
                          {provider.lastMessageTime && (
                            <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                              {formatTime(provider.lastMessageTime)}
                            </span>
                          )}
                        </div>
                        {provider.lastMessage ? (
                          <p className={`text-sm truncate ${provider.unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                            {provider.lastMessage}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No messages yet</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col">
            {selectedProvider ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {selectedProvider.full_name[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{selectedProvider.full_name}</p>
                        <p className="text-sm text-gray-600">{selectedProvider.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        selectedProvider.status === 'approved' ? 'bg-green-100 text-green-800' :
                        selectedProvider.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedProvider.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                        <i className="ri-chat-3-line text-4xl text-teal-600 w-10 h-10 flex items-center justify-center"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Messages Yet</h3>
                      <p className="text-gray-600 max-w-md">
                        Start the conversation by sending a message to {selectedProvider.full_name}
                      </p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender_type === 'admin' ? 'justify-end' : 'justify-start'} animate-[fadeIn_0.3s_ease-out]`}
                        >
                          <div className={`flex items-end space-x-2 max-w-md ${message.sender_type === 'admin' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                            {message.sender_type === 'provider' && (
                              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 flex-shrink-0 font-semibold text-sm">
                                {selectedProvider.full_name[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div
                                className={`px-4 py-2 rounded-2xl ${
                                  message.sender_type === 'admin'
                                    ? 'bg-teal-600 text-white rounded-br-none'
                                    : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none'
                                }`}
                              >
                                <p className="text-sm">{message.message}</p>
                              </div>
                              <p className={`text-xs mt-1 ${
                                message.sender_type === 'admin' ? 'text-right text-gray-500' : 'text-left text-gray-500'
                              }`}>
                                {formatTime(message.created_at)}
                              </p>
                            </div>
                            {message.sender_type === 'admin' && (
                              <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                                <i className="ri-shield-user-line text-sm w-4 h-4 flex items-center justify-center"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                    <button
                      type="submit"
                      disabled={loading || !newMessage.trim()}
                      className="w-12 h-12 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-105"
                    >
                      <i className="ri-send-plane-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <i className="ri-customer-service-2-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
                  <p className="text-lg font-semibold text-gray-900 mb-2">Select a Provider</p>
                  <p className="text-gray-600">Choose a provider from the list to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Support Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-message-3-line text-xl text-teal-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{providers.reduce((sum, p) => sum + p.unreadCount, 0)}</p>
              <p className="text-xs text-gray-600">Unread Messages</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-xl text-blue-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{providers.length}</p>
              <p className="text-xs text-gray-600">Total Providers</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-chat-check-line text-xl text-green-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{providers.filter(p => p.unreadCount === 0).length}</p>
              <p className="text-xs text-gray-600">All Caught Up</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-xl text-yellow-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{providers.filter(p => p.unreadCount > 0).length}</p>
              <p className="text-xs text-gray-600">Needs Response</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
